import dynamic from 'next/dynamic';
import styled from 'styled-components';
import { useCallback, useEffect, useState } from 'react';
import { useRouter } from 'next/router';

import { getAppliedFilters } from 'shared/utils/gridEventTrackingUtils';
import { getPriceSliderRange } from 'search/utils/getPriceSliderRange';
import { LocalizedCurrency } from 'shared/components/LocalizedCurrency';

import { SearchFiltersHoverAndSelectedStyle } from 'search/styles/SearchStyles';
import { sendTrackingEvent } from 'tracking/utils/sendTrackingEvent';
import { useEventContext } from 'tracking/hooks/useEventContext';
import { getConstructorCategoriesStrings } from 'search/enums/translations';

const Slider = dynamic(() => import('shared/components/Slider').then((mod) => mod.Slider));

function getPriceStateFromUrl(router, min, max) {
  const str = router.query?.priceMin;
  const strM = router.query?.priceMax;

  const range = [];
  if (!str || typeof str !== 'string') {
    range[0] = min;
  } else {
    range[0] = Number(str);
  }

  if (!strM || typeof strM !== 'string') {
    range[1] = max;
  } else {
    range[1] = Number(strM);
  }

  return range;
}

// eslint-disable-next-line camelcase
const ConsumerSearchFilterPriceDesktop = ({ name, priceMin, priceMax, displayName }: any) => {
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const router = useRouter();
  const { asPath, query } = router;
  const hasFilterApplied = useCallback(() => {
    return Boolean(query?.priceMax || query?.priceMin);
  }, [router]);

  const pathnameWithDynamicValuesIncluded: string = asPath?.split('?')[0];
  const eventTrackingEventContext = useEventContext();

  const range: [number, number] = [priceMin, priceMax];

  const initialStateFromUrl: any = useCallback(() => {
    return getPriceStateFromUrl(router, priceMin, priceMax);
  }, [router, priceMin, priceMax]);

  const [values, setValues] = useState<[number, number]>(initialStateFromUrl() as [number, number]);
  const [update, setUpdate] = useState(initialStateFromUrl());
  const [isSliderActive, setIsSliderActive] = useState<boolean>(
    Boolean(query?.priceMax || query?.priceMin),
  );

  useEffect(() => {
    setIsSliderActive(Boolean(query?.priceMax || query?.priceMin));
  }, [router]);

  const toggleFilter = () => {
    setIsFilterOpen((prevState) => !prevState);

    if (!isFilterOpen) {
      sendTrackingEvent('FILTER_EXPAND_TAP', {
        event_context: eventTrackingEventContext,
        filter_name: name,
        service_name: 'consumer_search',
        ...(query?.query && { search_string: query.query.toString() }),
      });
    }
  };

  const handleUpdate = (updatedValues) => {
    setUpdate(updatedValues);
  };

  const handleSlideStart = () => {
    setIsSliderActive(true);
  };

  const handleSlideEnd = (updatedValues: [number, number]) => {
    setValues(updatedValues);
    setIsSliderActive(true);

    const newQuery = { ...query };
    delete newQuery.prices;

    void router.push(
      {
        pathname: pathnameWithDynamicValuesIncluded,
        query: { ...newQuery, priceMin: updatedValues[0], priceMax: updatedValues[1] },
      },
      null,
      { shallow: true, scroll: true },
    );
    sendTrackingEvent('FILTER_APPLY_TAP', {
      event_context: eventTrackingEventContext,
      filter_name: name,
      service_name: 'consumer_search',
      filter_selection: `${updatedValues[0]}-${updatedValues[1]}`,
      ...(getAppliedFilters(query) && { applied_filters: getAppliedFilters(query) }),
      ...(query?.query && { search_string: query.query.toString() }),
    });
  };

  useEffect(() => {
    if (!router?.query?.priceMax && !router?.query?.priceMax) {
      setValues([priceMin, priceMax]);
      setUpdate([priceMin, priceMax]);
      setIsSliderActive(false);
    } else if (initialStateFromUrl?.length === 2) {
      setValues(initialStateFromUrl());
      setUpdate(initialStateFromUrl());
      setIsSliderActive(true);
    }
  }, [priceMin, priceMax]);

  return (
    <>
      <ConsumerSearchFilterPriceDesktop.Title
        data-qa="price_filter"
        onClick={toggleFilter}
        isFilterOpen={isFilterOpen}
        hasFilterApplied={hasFilterApplied()}
      >
        {getConstructorCategoriesStrings(displayName)}{' '}
        {isSliderActive && (
          <span data-qa="price_filter_selection">
            (
            <LocalizedCurrency price={update[0]} /> - <LocalizedCurrency price={update[1]} />
            {update[1] === 1000000 ? '+' : ''})
          </span>
        )}
      </ConsumerSearchFilterPriceDesktop.Title>
      {isFilterOpen && (
        <ConsumerSearchFilterPriceDesktop.Collapsible>
          <Slider
            data-qa="price_filter_slider"
            range={getPriceSliderRange(range)}
            handleSlideEnd={handleSlideEnd}
            handleSlideStart={handleSlideStart}
            handleUpdate={handleUpdate}
            step={5000}
            values={values}
          />
        </ConsumerSearchFilterPriceDesktop.Collapsible>
      )}
    </>
  );
};

ConsumerSearchFilterPriceDesktop.Title = styled.button<{
  isFilterOpen: boolean;
  hasFilterApplied: boolean;
}>`
  display: block;
  text-align: right;
  width: 100%;
  text-transform: capitalize;
  cursor: pointer;
  padding: 0 0 4px;
  ${({ isFilterOpen }) => isFilterOpen && SearchFiltersHoverAndSelectedStyle};
  ${({ hasFilterApplied }) => hasFilterApplied && SearchFiltersHoverAndSelectedStyle};

  &:hover {
    ${SearchFiltersHoverAndSelectedStyle};
  }
`;

ConsumerSearchFilterPriceDesktop.Collapsible = styled.div`
  display: flex;
  flex-direction: column;
  padding-bottom: 32px;
  padding-left: 32px;
`;

export default ConsumerSearchFilterPriceDesktop;
